// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { AppRootElementId, ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { LegacyMtrsTeamsServiceWorkerPath, WebUrlSegments } from "@ms/uno-constants/lib/local/TeamsConstants";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Controls
import { LoggingStrings } from "@ms/uno-constants/lib/local/LoggingConstants";
import { CreateTaskFromMessageShell } from "@ms/uno-shell/lib/local/controls/plannerShell/createTaskFromMessageShell/CreateTaskFromMessageShell";
import { PlannerShell } from "@ms/uno-shell/lib/local/controls/plannerShell/PlannerShell";
// Host Adaptor
import { FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
import { TeamsAdaptor } from "@ms/uno-hostadaptors/lib/local/metaOS/teams/TeamsAdaptor";
// Loaders
import { TeamsAppActionCreatorLoader } from "../loaders/TeamsAppActionCreatorLoader";
import { TeamsAppLogicModuleLoader } from "../loaders/TeamsAppLogicModuleLoader";
// Logic Modules
import { LogicModule } from "@ms/planner-logicmodules/lib/local/modules/LogicModule";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { WebPlatformPerformanceMarker } from "@ms/uno-telemetry/lib/local/performance/WebPlatformPerformanceMarker";
import { setMetaOSAppSessionIdForLogs } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { extractEntityRoutingIdFromSubRoute, logNotificationInfo } from "@ms/uno-routing";
import { sleep } from "@ms/uno-utilities/lib/local/ControlFlowUtilities";
import { PageTimingKey, setPageTiming } from "@ms/uno-utilities/lib/local/PASUtilities";
import { hasPathPrefix } from "@ms/uno-utilities/lib/local/WebUrlUtilities";
import React from "react";
import { createRoot } from "react-dom/client";
import { isSubRoutePrefixOf } from "../../utilities/AppBootstrapRouteUtils";
import { initDataPreFetcher, triggerCaeGraphProbeAsync } from "../../utilities/AppBootstrapUtils";
import { configureHostAdaptor, registerThemeChange, setupProviders, setupWorkerLoaderAndRegisterTheme, } from "../../utilities/MetaOSAppBootstrapSetupUtils";
/**
 * The production bootstrap function
 */
export async function bootstrap() {
    // Set up performance Marker and mark bootstrap
    const performanceMarker = new WebPlatformPerformanceMarker();
    performanceMarker.mark(MarkerTag.AppBootstrap);
    const hostAdaptor = new TeamsAdaptor(performanceMarker, LoggingStrings.ProductNameV2);
    const { loggers, configProvider, context, initialRoute, initialRouteFromDeepLink, loader, persistentStorage, telemetryLoggers, user } = await configureHostAdaptor(hostAdaptor, performanceMarker);
    setPageTiming(PageTimingKey.buildId, configProvider().sessionMetaData?.clientBuild);
    // here because AppContext lives in Constants and detectActiveWebUrlPrefix is in utilities which requires constants -> circular dependency if we try to set this within AppContext
    AppContext.setRuntimeConfig({
        activeWebUrlPrefix: hasPathPrefix(window.location.pathname, WebUrlSegments.PlannerServiceTeamsPathSegment)
            ? WebUrlSegments.PlannerServiceTeamsPathSegment
            : WebUrlSegments.LegacyMtrsTeamsPathSegment,
    });
    const { agentProviders, authModule, dispatcher, requestPrioritizer, serviceProviders, storeLoader } = setupProviders(configProvider, context, EngagementTelemetryConstants.TeamsApp, initialRoute, initialRouteFromDeepLink, hostAdaptor, loader, loggers, user, persistentStorage);
    // CAE probe: Use the Graph Planner service to trigger CAE enforcement.
    const caeProbePromise = triggerCaeGraphProbeAsync(configProvider, loggers, serviceProviders.plannerGraph);
    // Create logic module loader with the authModule instance from setupProviders
    // This ensures a single AuthModule instance is used across the app for consistent
    // token management, expiry checks, and caching
    const lmLoader = new TeamsAppLogicModuleLoader(loader, dispatcher, loggers, serviceProviders, storeLoader, hostAdaptor, configProvider, authModule);
    const acLoader = new TeamsAppActionCreatorLoader(configProvider, dispatcher, loader, lmLoader, loggers, storeLoader, hostAdaptor);
    // Trigger the data pre-fetch
    if (!configProvider().settings.disablePreFetcher) {
        initDataPreFetcher(acLoader.load(ActionCreator.DataPreFetch), initialRouteFromDeepLink, context, hostAdaptor, loggers, configProvider);
    }
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    if (clientFlavor !== ClientFlavor.TaskFromMessage) {
        setupHostCaching(configProvider, hostAdaptor, loggers, storeLoader.load(Store.Route), acLoader);
    }
    const { workerLoader, showDevTools } = setupWorkerLoaderAndRegisterTheme(acLoader, configProvider, context, dispatcher, hostAdaptor, loggers, requestPrioritizer, storeLoader, serviceProviders, loader, lmLoader, telemetryLoggers, authModule, persistentStorage);
    // Clear and unregister legacy TT service workers if the flight is enabled
    if (configProvider().flights.EnableLegacyTTServiceWorkerCleanup) {
        clearAndUnregisterServiceWorkers(loggers, "legacy TT", LegacyMtrsTeamsServiceWorkerPath);
    }
    // Clear and unregister Uno service worker if Evo SW flight is disabled
    if (!configProvider().flights.EnableServiceWorkerInEvo) {
        clearAndUnregisterServiceWorkers(loggers, "Uno", "/service-worker.js");
    }
    // Wait for CAE probe to complete before rendering. The Graph Planner service's
    // TokenRefreshAndRetryAjaxClient already handles CAE flows (claims challenge → token refresh → retry).
    await caeProbePromise;
    // Initialize Trouter module connection in a non-blocking fashion
    if (configProvider().flights.EnableTrouterIntegration) {
        lmLoader
            .load(LogicModule.TrouterScenario)
            .then((module) => module.initializeConnectionAsync())
            .catch((error) => {
            loggers.traceLogger.logTrace(0x1e0511e2 /* tag_4brh8 */, TraceLevel.Warning, `Failed to initialize Trouter module. [Error=${ErrorUtilities.getMessage(error)}]`);
        });
    }
    const rootElement = document.getElementById(AppRootElementId);
    if (!rootElement) {
        loggers.traceLogger.logTrace(0x1e15b051 /* tag_4f1br */, TraceLevel.Error, `Element with id ${AppRootElementId} not found in the DOM.`);
    }
    const root = createRoot(rootElement);
    root.render(clientFlavor === ClientFlavor.TaskFromMessage ? (React.createElement(CreateTaskFromMessageShell, { dispatcher: dispatcher, acLoader: acLoader, serviceProviders: serviceProviders, storeLoader: storeLoader, hostAdaptor: hostAdaptor, loggers: loggers, agentProviders: agentProviders, onLoad: () => hostAdaptor.notifyLoaded(), onSuccess: () => {
            if (!configProvider().flights.EnableTeamsNotifySuccessV2) {
                hostAdaptor.notifyRenderSuccess();
            }
        }, onFail: (message) => hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message }), configProvider: configProvider })) : (React.createElement(PlannerShell, { authModule: authModule, dispatcher: dispatcher, serviceProviders: serviceProviders, workerLoader: workerLoader, hostAdaptor: hostAdaptor, loggers: loggers, onLoad: () => hostAdaptor.notifyLoaded(), onSuccess: () => {
            if (!configProvider().flights.EnableTeamsNotifySuccessV2) {
                hostAdaptor.notifyRenderSuccess();
            }
        }, onFail: (message) => hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message }), configProvider: configProvider, showDevTools: showDevTools, agentProviders: agentProviders, acLoader: acLoader, storeLoader: storeLoader, isDevMode: false })));
}
/**
 * Setup load and unload handler for app caching
 * @param configProvider Provider for app configuration
 * @param hostAdaptor The host adaptor
 * @param loggers Loggers
 * @param routeStore Route store
 * @param acLoader Action creator loader
 */
function setupHostCaching(configProvider, hostAdaptor, loggers, routeStore, acLoader) {
    if (configProvider().settings.disableAppCaching) {
        return;
    }
    const onLoadCallback = async (newClientFlavor, newRoute, contentUrl, cachedLoadId) => {
        try {
            const refreshedContext = await hostAdaptor.getMetaOSAppContext();
            setMetaOSAppSessionIdForLogs(refreshedContext.app.host.sessionId, loggers.logHandler);
        }
        catch (error) {
            loggers.traceLogger.logTrace(0x1d7862df /* tag_34gl5 */, TraceLevel.Warning, `Failed to refresh MetaOS app session ID for telemetry. [Error=${ErrorUtilities.getMessage(error)}]`);
        }
        loggers.traceLogger.logTrace(0x1e19b183 /* tag_4g1gd */, TraceLevel.Info, `Teams cached app load. [ClientFlavor=${newClientFlavor}]`);
        // Override client flavor
        const currentClientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
        const clientFlavorChanged = newClientFlavor !== currentClientFlavor;
        AppContext.overrideClientFlavor(newClientFlavor);
        // Entity id before the cached load. Null for cases where entity id is not present such as myDay, myTasks etc.
        // Extract entity id based on the existing route (previous state before the cached load).
        const oldEntityId = extractEntityRoutingIdFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
        // Entity id after the cached load. Null for cases where entity id is not present such as myDay, myTasks.
        // Undefined for cases when client flavor changes where reboot would happen and determine the new entity id based on persisted route on new client flavor.
        let newEntityId = undefined;
        if (clientFlavorChanged) {
            // If there's a client flavor update, re-boot the app to ensure the correct client flavor is used across dependencies
            // When the app is brought out of cache, teams SDK invoke the onLoad handler that the app provids when it registered.
            // The onLoad handler from Teams SDK will pass the new contentUrl. It is up to the app to use this info to navigate to the correct context.
            // Note: Updating the href will trigger the re-boot of the app. With local testing we have seen the JS bundles are being picked up from disk cache.
            // This is required on client change only as other cases are handled with below -
            // 1. Tab <-> Tab switches - Updating the route with uno's routing action creator
            // 2. App <-> App switches - No route change required as the app is loaded with on the same route
            window.location.href = contentUrl;
        }
        else {
            // If new route is provided within same client flavor (switch between different tabs), navigate to the new route
            if (newRoute != null) {
                logNotificationInfo(newRoute, loggers);
                newEntityId = extractEntityRoutingIdFromSubRoute(newRoute.subRoute, newRoute.subRouteType);
                // On Teams, a cached-load can fire for reasons other than a real tab switch (e.g. Teams Canary
                // restores the app after `openFilePreview` closes). In that case the newly parsed route is the
                // tab's default (plan-only) route, and navigating to it would dispatch NavigateToView which
                // ModalStore uses to close page-scoped modals — including an open Task Details editor.
                // When the cached load lands on the same tab (same entity id and same subRouteType) and the
                // new subRoute is a prefix of the current one, preserve the current in-tab state instead.
                const currentSubRoute = routeStore.getCurrentSubRoute();
                const currentSubRouteType = routeStore.getCurrentSubRouteType();
                const isSameTabCachedLoad = !configProvider().changeGates.DisablePreserveRouteOnSameTabCachedLoad &&
                    newClientFlavor === ClientFlavor.Tab &&
                    newEntityId != null &&
                    newEntityId === oldEntityId &&
                    newRoute.subRouteType === currentSubRouteType &&
                    isSubRoutePrefixOf(newRoute.subRoute, currentSubRoute);
                if (isSameTabCachedLoad) {
                    loggers.traceLogger.logTrace(0x1d801720 /* tag_36b26 */, TraceLevel.Info, `Teams cached app load - Same tab, preserving current route.`);
                }
                else {
                    const routeActionCreator = acLoader.load(ActionCreator.Routing);
                    routeActionCreator.navigate(newRoute);
                }
            }
            else {
                // For flavors (such as App) where the newRoute is null i.e. no route change required, the entity id remains the same.
                newEntityId = oldEntityId;
            }
            // Update app context and re-register theme change handler.
            // This is not required when client flavor changes as the app will be re-booted and handle them inherently.
            setTimeout(async () => {
                // [WI #9580747]: onLoadCallback is invoked before the viewport is set for the app.
                // Triggering below store update before the viewport is set could cause incorrect rendering of the components.
                const appContextActionCreator = acLoader.load(ActionCreator.AppContext);
                // Execute the theme update after a delay to ensure the app has been fully loaded before triggering the update.
                appContextActionCreator.fetchAppContext();
                appContextActionCreator.updateAppBackgroundState(false); // Update background state to false as the app is loaded in foreground
            }, 100);
            registerThemeChange(hostAdaptor, acLoader);
            // Call notify success to indicate that the app has been successfully loaded from cache
            hostAdaptor.notifyRenderSuccess();
        }
        loggers.performanceMarker.mark(MarkerTag.CachedLoadComplete, {
            cachedLoadId, // Unique identifier for the particular cached load instance
            oldFlavor: currentClientFlavor, // Client flavor before the cached load
            newFlavor: newClientFlavor, // Client flavor after the cached load
            oldEntityId: oldEntityId ?? "", // Entity id before the cached load. Empty string for cases where entity id is not present.
            newEntityId: newEntityId ?? "", // Entity id after the cached load. Empty string for cases where entity id is not present.
        });
    };
    const onUnloadCallback = async (notifyUnloadSuccess) => {
        // TODO: Invoke the background API on IViewRender once implemented for each view to perform clean up.
        const appContextActionCreator = acLoader.load(ActionCreator.AppContext);
        appContextActionCreator.updateAppBackgroundState(true); // Update background state to true as the app is moving to background
        notifyUnloadSuccess();
    };
    hostAdaptor.setupHostCaching(onLoadCallback, onUnloadCallback);
}
/**
 * Sends a message to any running service worker registrations to clear their caches and unregisters them
 * @param loggers Logger context for telemetry
 * @param workerType App type of service worker for logging purposes (e.g., "legacy TT", "Uno")
 * @param path Service worker path to look for
 */
async function clearAndUnregisterServiceWorkers(loggers, workerType, path) {
    // The specific command to send to the service worker to clear its caches and unregister
    const unregisterCommand = {
        command: "clearAndUnregister",
    };
    // Check that service workers are supported
    if (!("serviceWorker" in navigator) || typeof navigator.serviceWorker.getRegistrations !== "function") {
        // no-op, service workers are not supported
        return;
    }
    try {
        const regs = await navigator.serviceWorker.getRegistrations();
        const targetRegs = regs.filter((reg) => reg.installing?.scriptURL?.includes(path) || reg.active?.scriptURL?.includes(path));
        if (targetRegs.length > 0) {
            await Promise.all(targetRegs.map(async (reg) => {
                if (reg.installing) {
                    reg.installing.postMessage(unregisterCommand);
                    loggers.traceLogger.logTrace(0x1e103695 /* tag_4ed0v */, TraceLevel.Info, `Tell ${workerType} ServiceWorker to clear out [state=${reg.installing.state}]`);
                }
                if (reg.active) {
                    reg.active.postMessage(unregisterCommand);
                    loggers.traceLogger.logTrace(0x1e103694 /* tag_4ed0u */, TraceLevel.Info, `Tell ${workerType} ServiceWorker to clear out [state=${reg.active.state}]`);
                }
                // Wait for 5s to allow the worker to clear caches before unregistering
                await sleep(5000);
                const result = await reg.unregister();
                loggers.traceLogger.logTrace(0x1e103693 /* tag_4ed0t */, TraceLevel.Info, `${workerType} ServiceWorker unregistration done [result=${result}]`);
            }));
            loggers.traceLogger.logTrace(0x1e103692 /* tag_4ed0s */, TraceLevel.Info, `${workerType} ServiceWorker unregistrations done [count=${targetRegs.length}]`);
        }
    }
    catch (e) {
        loggers.traceLogger.logTrace(0x1e103691 /* tag_4ed0r */, TraceLevel.Warning, `${workerType} ServiceWorker unregistrations failed [reason=${ErrorUtilities.getMessage(e)}]`);
    }
}
